/**
 * useAdaptiveSplit - Adaptive height limit for the phone action panel.
 *
 * The phone screen is split into two zones: game info (top) and the action panel
 * (bottom). The panel must never squeeze the info zone below half of the screen,
 * but it may take more than half when the info content genuinely does not need it.
 *
 * Game modes declare "I fill the available area" using different techniques
 * (flexbox `flex-1`, CSS grid `1fr`, `height: 100%`, `absolute inset-0`), and none
 * of them is reliably readable from resolved styles after layout. Instead of
 * inspecting declarations, this hook probes layout BEHAVIOUR: it measures the
 * content at two different parent heights. Content whose height follows the parent
 * is elastic (panel capped at 50%); content whose height stays constant is measured
 * against a threshold: only content short enough to be a status line lets the
 * panel exceed half, anything taller is capped at 50% as well.
 *
 * Known limitation (accepted): the natural-height measurement runs before images
 * inside the content finish loading. Structural changes to the direct children are
 * re-measured via MutationObserver, and content built deeper by mode scripts is
 * covered by the caller forcing a measurement once those scripts have run; image
 * loads are covered by neither, which for rare natural-height content with images
 * can leave the panel slightly too tall.
 */

import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from "react"

/** Tolerance in px for comparing the two probe measurements. */
const PROBE_EPSILON = 1

/**
 * Upper bound (px) of info content that still counts as a status line — the only
 * case, next to an empty info zone, in which the action panel may exceed half the
 * area. An absolute value, not a fraction of the screen: the rule is expressed in
 * lines of text, so it must mean the same thing on every device. Sized at roughly
 * four lines: a status line plus the spacing a game mode wraps it in.
 */
const STATUS_LINE_THRESHOLD = 64

/**
 * Measures the rendered vertical span of a game mode's HTML.
 *
 * A game mode may emit multiple root elements (e.g. AI Story combines the
 * narrative prompter with a skill panel), so the span from the top of the first
 * rendered child to the bottom of the last one is used — this includes the gaps
 * and margins between roots, unlike a plain sum of heights.
 *
 * Non-rendered children are skipped: game mode HTML routinely opens with <style>
 * and closes with <script>, both of which report an all-zero rectangle and would
 * otherwise collapse the span to zero regardless of the content in between.
 *
 * @returns The span in px, or null when nothing is rendered — an info zone that
 *   holds only <style>/<script> is as empty as one with no children at all.
 */
function measureContentSpan(element: HTMLElement): number | null {
  const rendered = Array.from(element.children).filter(
    (child) => child.getClientRects().length > 0,
  )
  if (rendered.length === 0) return null
  const first = rendered[0].getBoundingClientRect()
  const last = rendered[rendered.length - 1].getBoundingClientRect()
  return last.bottom - first.top
}

/**
 * Decides how much height the action panel may take.
 *
 * @param availableHeight Height of the content area holding both zones.
 * @param contentSpan Measured span of the info content, or null when the info
 *   zone is empty (host unmounted or without children).
 * @param infoPadding Vertical padding of the info zone wrapper. Not read when
 *   `contentSpan` is null — there is no content to leave room for.
 * @param isElastic Whether the content height follows its parent. Not read when
 *   `contentSpan` is null — nothing was probed.
 */
export function resolvePanelMaxHeight(
  availableHeight: number,
  contentSpan: number | null,
  infoPadding: number,
  isElastic: boolean,
): number {
  // Nothing to show in the info zone: the panel may use the whole area.
  if (contentSpan === null) return availableHeight

  const halfHeight = availableHeight / 2
  if (isElastic || contentSpan > STATUS_LINE_THRESHOLD) return halfHeight

  // Status-line content: leave the info zone its content plus the wrapper padding.
  return Math.max(halfHeight, availableHeight - contentSpan - infoPadding)
}

interface ScrollPosition {
  element: Element
  scrollTop: number
}

/**
 * Captures the scroll offset of an element and of every scrolled descendant.
 *
 * The probe shrinks the host, which also shrinks whatever the game mode scrolls
 * inside it (the szpiedzy scratchpad, the bingo card area). While those boxes are
 * short the browser clamps their scroll offset, and growing them back does not
 * restore it — without this the player's scratchpad jumps to the top on every
 * keyboard opening, rotation or content update.
 */
function captureScrollPositions(root: HTMLElement): ScrollPosition[] {
  const positions: ScrollPosition[] = [{ element: root, scrollTop: root.scrollTop }]
  for (const element of root.querySelectorAll("*")) {
    if (element.scrollTop > 0) positions.push({ element, scrollTop: element.scrollTop })
  }
  return positions
}

/** Sum of top + bottom padding of an element, in px. */
function verticalPadding(element: HTMLElement): number {
  const style = getComputedStyle(element)
  return parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
}

/**
 * Computes the maximum height (px) the action panel may occupy.
 *
 * @param containerRef Content area below the phone header (holds both zones).
 * @param infoContentRef Element holding the game mode HTML.
 * @param contentKey Changes whenever new info HTML is rendered; triggers re-measure.
 * @returns `panelMaxHeight` — max panel height in px, zero before the first
 *   measurement (the caller falls back to a static 50% cap) — and `remeasure`,
 *   a stable callback for forcing a measurement once mode scripts have built
 *   their DOM, which no observer would otherwise notice.
 */
export function useAdaptiveSplit(
  containerRef: RefObject<HTMLElement | null>,
  infoContentRef: RefObject<HTMLElement | null>,
  contentKey: string,
): { panelMaxHeight: number; remeasure: () => void } {
  const [panelMaxHeight, setPanelMaxHeight] = useState(0)

  const measure = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const availableHeight = container.clientHeight
    if (availableHeight <= 0) return

    const halfHeight = availableHeight / 2
    const infoContent = infoContentRef.current

    // No info host mounted (e.g. loading spinner): nothing to probe, bound the
    // panel to the full area so it scrolls internally instead of overflowing.
    if (!infoContent) {
      setPanelMaxHeight(resolvePanelMaxHeight(availableHeight, null, 0, false))
      return
    }

    // Behaviour probe: measure at full height, then at half height. Resizing the
    // host clamps scroll offsets inside it, so they are restored afterwards.
    const wrapper = infoContent.parentElement
    const infoPadding = wrapper ? verticalPadding(wrapper) : 0
    const originalHeight = infoContent.style.height
    const scrollPositions = captureScrollPositions(infoContent)

    infoContent.style.height = `${availableHeight}px`
    const spanAtFull = measureContentSpan(infoContent)
    infoContent.style.height = `${halfHeight}px`
    const spanAtHalf = measureContentSpan(infoContent)
    infoContent.style.height = originalHeight
    for (const position of scrollPositions) {
      position.element.scrollTop = position.scrollTop
    }

    const isElastic =
      spanAtFull !== null && spanAtHalf !== null && Math.abs(spanAtFull - spanAtHalf) > PROBE_EPSILON
    setPanelMaxHeight(resolvePanelMaxHeight(availableHeight, spanAtFull, infoPadding, isElastic))
  }, [containerRef, infoContentRef])

  // Coalesce bursts of triggers (keyboard animation, rotation, DOM mutations,
  // forced re-measures) into one measure per frame to avoid layout thrash.
  const frameRef = useRef(0)
  const schedule = useCallback(() => {
    if (frameRef.current) return
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = 0
      measure()
    })
  }, [measure])

  // The container outlives every content change, so this observer is attached
  // once for the lifetime of the component.
  useLayoutEffect(() => {
    const container = containerRef.current

    // Observing the container only: the probe mutates an inner element, which
    // cannot change the container height, so no feedback loop is possible.
    let resizeObserver: ResizeObserver | null = null
    if (container) {
      resizeObserver = new ResizeObserver(schedule)
      resizeObserver.observe(container)
    }

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current)
      resizeObserver?.disconnect()
    }
  }, [schedule, containerRef])

  // Re-measure when top-level content structure changes after the initial
  // render (e.g. unsafe <script> swaps that add root nodes). Direct children
  // only: deeper cosmetic mutations — animated-emoji host swaps inside text,
  // script-built grids — are excluded to avoid needless reflow, and such
  // content is elastic (capped at 50%) so its exact height is irrelevant.
  // Keyed by contentKey: the host is not mounted on the first render (the view
  // shows a spinner until HTML arrives), so the observer has to be attached
  // again once the content is there.
  useLayoutEffect(() => {
    const infoContent = infoContentRef.current
    if (!infoContent) return

    const mutationObserver = new MutationObserver(schedule)
    mutationObserver.observe(infoContent, { childList: true })

    return () => mutationObserver.disconnect()
  }, [schedule, infoContentRef, contentKey])

  // Re-measure whenever new info HTML is rendered.
  useLayoutEffect(() => {
    measure()
  }, [measure, contentKey])

  return { panelMaxHeight, remeasure: schedule }
}
